import type { IContact } from "@bbj-types/contact";
import { ContactType } from "@bbj-types/contact/enum";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContactsIcon from "@mui/icons-material/ContactsOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import {
  CircularProgress,
  Fab,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { PaperCollapse } from "../../surfaces/PaperCollapse";
import { ContactInfo } from "./contactInfo";
import { ContactPickerCreate } from "./create";
import { useContactPicker } from "./logics";
import { ContactPickerSelect } from "./select";
import type { ContactPickerEnterprise } from "./types";

interface IContactPickerProps {
  className?: string;
  type?: ContactType;
  enterprise: ContactPickerEnterprise | null;
  value: IContact | null;
  onChange: (contact: IContact | null) => void;
  isSharedEnterprise?: boolean;
  disabled?: boolean;
}

export const ContactPicker = ({
  className,
  type = ContactType.ENTERPRISE,
  enterprise,
  value,
  onChange,
  isSharedEnterprise = false,
  disabled = false,
}: IContactPickerProps) => {
  const {
    expanded,
    setExpanded,
    loading,
    contactsNumber,
    handleSubmit,
    handleCancel,
    contact,
    contextContact,
    alreadyExistsContact,
    setContact,
    mode,
    changeMode,
    error,
  } = useContactPicker({ type, enterprise, value, onChange, isSharedEnterprise });

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (contact) {
      setContact((contact) => contact && { ...contact, isTemp: !isSharedEnterprise });
    } else if (contextContact) {
      onChange({ ...contextContact, isTemp: !isSharedEnterprise });
    }
  }, [isSharedEnterprise]);

  return (
    <Stack className={className}>
      <PaperCollapse
        open={expanded}
        onOpen={(v) => setExpanded(v)}
        ButtonProps={{ elevation: 2 }}
        actionButtons={
          contextContact && (
            <>
              {!contextContact._id && (
                <IconButton
                  onClick={() => {
                    setExpanded(true);
                    changeMode("create");
                  }}
                >
                  <EditIcon color="primary" />
                </IconButton>
              )}
              <IconButton onClick={() => onChange(null)}>
                <DeleteIcon color="primary" />
              </IconButton>
            </>
          )
        }
        title={
          contextContact ? (
            <ContactInfo contact={contextContact} disableDescription />
          ) : (
            <Typography variant="body1SemiBold">Contact</Typography>
          )
        }
        openIcon={enterprise && !contextContact ? { open: <AddIcon />, close: <CloseIcon /> } : undefined}
        category={{
          icon: <ContactsIcon htmlColor="white" />,
          iconBackgroundColor: (theme) => theme.palette.success.main,
        }}
        disabled={disabled || !enterprise || !!contextContact}
      >
        <Stack
          sx={{
            alignItems: "stretch",
          }}
        >
          {contactsNumber > 0 ? (
            <Typography variant="body1">
              {contactsNumber} contact{contactsNumber > 1 ? "s" : ""} {contactsNumber > 1 ? "sont liés" : "est lié"} à
              cette entreprise. Sélectionnez-{contactsNumber > 1 ? "en un" : "le"} ou créez-en un nouveau :
            </Typography>
          ) : (
            <Typography variant="body1">Aucun contact n'est lié à cette entreprise. Créez-en un nouveau :</Typography>
          )}
          <FormControl>
            <RadioGroup value={mode} onChange={(_, value) => changeMode(value)}>
              <FormControlLabel
                value="select"
                control={<Radio />}
                disabled={contactsNumber <= 0}
                label={
                  <Typography variant="body1SemiBold" color="primary">
                    Contact(s) existant(s)
                  </Typography>
                }
              />
              <ContactPickerSelect
                mode={mode}
                contact={contact}
                setContact={setContact}
                contacts={enterprise?.contacts}
                hidden={mode !== "select"}
              />
              <FormControlLabel
                value="create"
                control={<Radio />}
                label={
                  <Typography variant="body1SemiBold" color="primary">
                    Nouveau contact
                  </Typography>
                }
              />
              <ContactPickerCreate
                contact={contact}
                enterprise={enterprise}
                alreadyExistsContact={alreadyExistsContact}
                setContact={setContact}
                hidden={mode !== "create"}
                error={error}
              />
            </RadioGroup>
          </FormControl>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "end",
              gap: 1,
            }}
          >
            <Fab color="white" size="small" onClick={handleCancel} disabled={loading}>
              <CloseIcon sx={(theme) => ({ color: theme.palette.primary.dark })} />
            </Fab>
            <Fab color="white" size="small" onClick={async () => await handleSubmit()} disabled={loading}>
              {loading ? (
                <CircularProgress color="primary" size="16px" />
              ) : (
                <CheckIcon sx={(theme) => ({ color: theme.palette.success.main })} />
              )}
            </Fab>
          </Stack>
        </Stack>
      </PaperCollapse>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          display: mode === "create" && (isSharedEnterprise || enterprise?._id) && contextContact ? "flex" : "none",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={!contextContact?.isTemp}
              onChange={(_, checked) => contextContact && onChange({ ...contextContact, isTemp: !checked })}
            />
          }
          label={
            <Typography variant="body1SemiBold">Voulez-vous ajouter ce contact à la page de l'entreprise ?</Typography>
          }
          labelPlacement="start"
        />
      </Stack>
    </Stack>
  );
};
